'use client';

export const STORAGE_KEYS = {
    RECENTLY_PLAYED_GAMES: 'RECENTLY_PLAYED_GAMES',
    ACCOUNT_UM_SESSION: 'ACCOUNT_UM_SESSION',
    EXCLUSIVE_POPUP_SEEN_ZONES: 'EXCLUSIVE_POPUP_SEEN_ZONES',
    SESSION_CASINO: 'session-casino',
    NAVIGATION_SOURCE: 'navigationSource',
    SCROLL_POSITION: 'scrollPosition',
    ROTATING_TITLE_HISTORY: 'ROTATING_TITLE_HISTORY'
};

export type StoreKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

export type StorageItem = {
    key: StoreKey;
    value: string;
};

export type StorageType = 'local' | 'session';

const getStorage = (storageType: StorageType): Storage =>
    storageType === 'local' ? window.localStorage : window.sessionStorage;

// Storage access throws when it is blocked (sandboxed iframe, hardened privacy settings, locked-down
// WebView) or unavailable during SSR. Callers treat storage as best-effort: reads fall back to null,
// writes and removes become no-ops instead of crashing.
export const store = (item: StorageItem, storageType: StorageType = 'session'): void => {
    try {
        getStorage(storageType).setItem(item.key, item.value);
    } catch {
        // storage unavailable — nothing to persist
    }
};

export const retrieve = (key: StoreKey, storageType: StorageType = 'session'): string | null => {
    try {
        return getStorage(storageType).getItem(key);
    } catch {
        return null;
    }
};

export const remove = (key: StoreKey, storageType: StorageType = 'session'): void => {
    try {
        getStorage(storageType).removeItem(key);
    } catch {
        // storage unavailable — nothing to remove
    }
};

export const getSessionId = (): string | null => {
    try {
        const sessionData = retrieve(STORAGE_KEYS.SESSION_CASINO, 'local');
        if (!sessionData) {
            return null;
        }

        const session = JSON.parse(sessionData);
        return session?.sessionId || null;
    } catch {
        return null;
    }
};
